import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { truncate, sortBy, find, map, filter } from 'lodash';
import sortByChapter from 'sort-by-chapter';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import removeHtml from '../util/remove_html_markup';
import buildPageTitle from '../util/buildPageTitle';
import Loader from './loader';
import Homepage from './page/homepage';
import DrillDownPage from './page/drillDownPage';
import { PREVIEW_PAGE_DATA } from '../graphqls';

export default function Preview({ branding, linkPrefix }) {
  const { subdomain: key, pageId = 'home' } = useParams();
  const {
    loading: loadingPreviewData,
    data: {
      previewPageData: { pages = {}, generalSettings = {}, generalSettings: { customFields = [], tags = [] } = {}, expiredAt } = {},
    } = {},
    error,
  } = useQuery(PREVIEW_PAGE_DATA, {
    variables: { key, pageId },
    onCompleted({
      previewPageData: {
        generalSettings: { pageTitle: orgTitle } = {},
        pages: completedPages = {},
      },
    }) {
      document.title = buildPageTitle(removeHtml(completedPages[pageId]?.title || ''), orgTitle);
    },
  });
  const pageArray = JSON.parse(JSON.stringify(Object.values(pages)));
  const pageObject = {};
  let tree = [];
  const level1PageIds = map(filter(pageArray, ['parentPlanNodeId', '']), 'planNodeId');
  const level2PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level1PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );
  const level3PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level2PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );
  const level4PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level3PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );

  pageArray.forEach((_, pageIndex) => {
    pageObject[pageArray[pageIndex].planNodeId] = pageIndex;
    pageArray[pageIndex].items = [];
    pageArray[pageIndex].measures = [];
  });
  pageArray.forEach(
    ({
      parentPlanNodeId,
      measureId,
      planNodeId,
      title,
      description,
      numberWithLabel,
      position,
      latestUpdate,
      additionalBottomText,
      additionalMiddleText,
      additionalUpperMiddleText,
      additionalText,
      customFieldRecords,
      tags: taggings,
    }) => {
      const customFieldRecordContents = (customFieldRecords || [])
        .map(({ customFieldId, content }) => {
          const currentField = find(customFields || [], { customFieldId }) || {};

          if (
            ['TEXT', 'RICH_TEXT', 'NUMBER', 'SINGLE_SELECT_PICKLIST', 'MULTI_SELECT_PICKLIST'].includes(
              (currentField?.fieldType || '').toUpperCase(),
            )
          ) {
            return [currentField?.fieldName || '', content || ''].join(' ');
          }

          return '';
        })
        .join(' ');
      const tagNames = (taggings || []).map((tagId) => find(tags, { tagId })?.tagName || '').join(' ');

      if (typeof parentPlanNodeId === 'string' && parentPlanNodeId.length > 0) {
        if (pageArray[pageObject[parentPlanNodeId]]) {
          let level = 0;

          if (level1PageIds.includes(planNodeId)) {
            level = 1;
          } else if (level2PageIds.includes(planNodeId)) {
            level = 2;
          } else if (level3PageIds.includes(planNodeId)) {
            level = 3;
          } else if (level4PageIds.includes(planNodeId)) {
            level = 4;
          }

          pageArray[pageObject[parentPlanNodeId]].items.push({
            id: planNodeId,
            title: title || truncate(removeHtml(description), { separator: ' ', length: 60 }),
            isMeasure: false,
            numberWithLabel,
            items: pageArray[pageObject[planNodeId]].items,
            measures: pageArray[pageObject[planNodeId]].measures,
            level,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields: customFieldRecordContents,
            tags: tagNames,
          });
        }
      } else if (typeof measureId === 'string') {
        pageArray[pageObject[planNodeId]].measures.push({
          id: measureId,
          title,
          isMeasure: true,
          position,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields: customFieldRecordContents,
          tags: tagNames,
        });
      } else if (parentPlanNodeId === '') {
        tree.push({
          id: planNodeId,
          title: title || truncate(removeHtml(description), { separator: ' ', length: 60 }),
          level: 1,
          isMeasure: false,
          items: pageArray[pageObject[planNodeId]].items,
          measures: pageArray[pageObject[planNodeId]].measures,
          numberWithLabel,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields: customFieldRecordContents,
          tags: tagNames,
        });
      }
    },
  );
  tree = sortByChapter(tree, { attribute: 'numberWithLabel' }).map(({ items: strategies, measures: goalMeasures, ...goalAttributes }) => ({
    items: sortByChapter(strategies, { attribute: 'numberWithLabel' }).map(
      ({ items: activities, measures: strategyMeasures, ...strategyAttributes }) => ({
        items: sortByChapter(activities, { attribute: 'numberWithLabel' }).map(({ measures: activityMeasures, ...activityAttributes }) => ({
          measures: sortBy(activityMeasures, ['position']),
          ...activityAttributes,
        })),
        measures: sortBy(strategyMeasures, ['position']),
        ...strategyAttributes,
      }),
    ),
    measures: sortBy(goalMeasures, ['position']),
    ...goalAttributes,
  }));

  // building search source
  const searchList = [];

  tree.forEach(
    ({
      id,
      title,
      measures,
      items,
      latestUpdate,
      additionalBottomText,
      additionalMiddleText,
      additionalUpperMiddleText,
      additionalText,
      description,
      customFields,
      tags,
    }) => {
      searchList.push({
        id,
        isMeasure: false,
        title,
        latestUpdate,
        additionalBottomText,
        additionalMiddleText,
        additionalUpperMiddleText,
        additionalText,
        description,
        customFields,
        tags,
      });
      measures.forEach(
        ({
          id: measureId,
          title: measureTitle,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields,
        }) => {
          searchList.push({
            id: measureId,
            isMeasure: true,
            title: measureTitle,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields,
            tags,
          });
        },
      );
      items.forEach(
        ({
          id: strategyId,
          title: strategyTitle,
          measures: strategyMeasures,
          items: activities,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields,
          tags,
        }) => {
          searchList.push({
            id: strategyId,
            isMeasure: false,
            title: strategyTitle,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields,
            tags,
          });
          strategyMeasures.forEach(
            ({
              id: measureId,
              title: measureTitle,
              latestUpdate,
              additionalBottomText,
              additionalMiddleText,
              additionalUpperMiddleText,
              additionalText,
              description,
              customFields,
              tags,
            }) => {
              searchList.push({
                id: measureId,
                isMeasure: true,
                title: measureTitle,
                latestUpdate,
                additionalBottomText,
                additionalMiddleText,
                additionalUpperMiddleText,
                additionalText,
                description,
                customFields,
                tags,
              });
            },
          );
          activities.forEach(
            ({
              id: activityId,
              title: activityTitle,
              measures: activityMeasures,
              latestUpdate,
              additionalBottomText,
              additionalMiddleText,
              additionalUpperMiddleText,
              additionalText,
              description,
              customFields,
              tags,
            }) => {
              searchList.push({
                id: activityId,
                isMeasure: false,
                title: activityTitle,
                latestUpdate,
                additionalBottomText,
                additionalMiddleText,
                additionalUpperMiddleText,
                additionalText,
                description,
                customFields,
                tags,
              });
              activityMeasures.forEach(
                ({
                  id: measureId,
                  title: measureTitle,
                  latestUpdate,
                  additionalBottomText,
                  additionalMiddleText,
                  additionalUpperMiddleText,
                  additionalText,
                  description,
                  customFields,
                  tags,
                }) => {
                  searchList.push({
                    id: measureId,
                    isMeasure: true,
                    title: measureTitle,
                    latestUpdate,
                    additionalBottomText,
                    additionalMiddleText,
                    additionalUpperMiddleText,
                    additionalText,
                    description,
                    customFields,
                    tags,
                  });
                },
              );
            },
          );
        },
      );
    },
  );

  const pageData = pages[pageId] || {};
  let pageLevel = 1;

  if (pageId === 'home') {
    pageLevel = 0;
  } else if (pageData.ancestor) {
    pageLevel = pageData.ancestor.length + (pageData.measureId ? 0 : 1);
  }

  if (error) {
    console.error(error);
    window.location.href = 'https://app.envisio.com/404';
  }

  if (loadingPreviewData) {
    return <Loader loadingMessage="We're preparing the content...this will only take a minute." />;
  }

  return (
    <div className={`${['page', pageId].join('-')} ${pageData.measureId ? 'measure-' : ''}level-${pageLevel}`}>
      <div className="preview-strip">
        <strong>This is a preview of your public dashboard</strong>
        <span>{`Expiry Date:${moment(expiredAt).format('llll')}`}</span>
      </div>
      {pageId === 'home' ? (
        <Homepage data={pageData} settings={{ ...generalSettings }} linkPrefix={linkPrefix} branding={branding} navs={tree} />
      ) : (
        <DrillDownPage
          data={pageData}
          ancestor={pageData.ancestor}
          settings={{ ...generalSettings }}
          linkPrefix={linkPrefix}
          branding={branding}
          navs={tree}
        />
      )}
    </div>
  );
}

Preview.propTypes = {
  linkPrefix: PropTypes.string.isRequired,
  branding: PropTypes.bool.isRequired,
};
